import React, { useState } from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Label } from "@progress/kendo-react-labels";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import {
  MdOutlineMailOutline,
  MdOutlinePhone,
  MdOutlineLocationOn,
  MdOutlinePerson,
} from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";

import { Tile } from "components/UIComponents/Tile";
import { TileWrapper } from "components/UIComponents/TileWrapper";
import ClientComboBox from "components/common/ClientComboBox";
import AddClients from "pages/Clients/AddViewClients";
import ConfirmationDialog from "components/common/ConfirmationDialog";
import SubmitButton from "components/common/SubmitButton";
import ViewLogo from "assets/view-icon.svg";

const CLIENT_TYPE_LABELS = { 0: "Person", 1: "Company" };
const formatClientType = (value) => {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "number") return CLIENT_TYPE_LABELS[value] ?? "";
  if (typeof value === "string" && /^\d+$/.test(value))
    return CLIENT_TYPE_LABELS[Number(value)] ?? value;
  return value;
};

const IconBadge = ({ children }) => (
  <span
    className="d-inline-flex align-items-center justify-content-center rounded-circle bgBtnSecondary"
    style={{ width: 36, height: 36, color: "#000000", flexShrink: 0 }}
  >
    {children}
  </span>
);

const LeadClientInfoTile = ({ leadClient }) => (
  <Tile heading="Lead Client" className="px-4 pt-2 pb-3 overflow-auto h-100">
    <div className="row pt-2 g-3">
      <div className="col-4 d-flex align-items-center gap-3">
        <IconBadge><MdOutlinePerson size={18} /></IconBadge>
        <span className="text-truncate fw-semibold">
          {[leadClient?.clientRef, leadClient?.clientName].filter(Boolean).join(" — ") || "—"}
        </span>
      </div>
      <div className="col-4 d-flex align-items-center gap-3">
        <IconBadge><MdOutlineMailOutline size={18} /></IconBadge>
        <span className="text-truncate fw-semibold">{leadClient?.email || "—"}</span>
      </div>
      <div className="col-4 d-flex align-items-center gap-3">
        <IconBadge><MdOutlinePhone size={18} /></IconBadge>
        <span className="text-truncate fw-semibold">{leadClient?.phone || "—"}</span>
      </div>
      <div className="col-4 d-flex align-items-center gap-3">
        <IconBadge><MdOutlineLocationOn size={18} /></IconBadge>
        <span className="text-truncate fw-semibold">{leadClient?.address || "—"}</span>
      </div>
    </div>
  </Tile>
);

const ClientsGridTile = ({ rows, isView, onRemove, onView, onAddClick }) => {
  const ActionCell = (cellProps) => (
    <td className="text-center">
      <div className="d-flex align-items-center justify-content-center gap-3">
        <img
          src={ViewLogo}
          alt="View"
          title="View Client"
          role="button"
          className="cursor-pointer"
          style={{ width: 18, height: 18 }}
          onClick={() => onView?.(cellProps.dataItem)}
        />
        <span
          role="button"
          title="Remove"
          className="cursor-pointer"
          onClick={() => onRemove?.(cellProps.dataItem)}
        >
          <RiDeleteBin6Line size={18} color="#d32f2f" />
        </span>
      </div>
    </td>
  );

  return (
    <Tile
      heading="Client Details"
      className="px-4 pt-2 pb-3 d-flex flex-column h-100"
      minHeight={300}
      headingItems={
        <Button
          type="button"
          className="button-small bgBtnSecondary fw-bold me-1"
          onClick={onAddClick}
        >
          Add Client
        </Button>
      }
    >
      <div className="mt-2 flex-grow-1 d-flex flex-column overflow-auto">
        <Grid data={rows || []} style={{ height: "100%" }}>
          <GridColumn field="clientRef" title="Client Ref" width="100px" />
          <GridColumn field="clientName" title="Client Name" width="200px" />
          <GridColumn field="feeEarnerRef" title="Fee Earner" width="100px" />
          <GridColumn
            field="type"
            title="Type"
            width="80px"
            cell={(cellProps) => <td>{formatClientType(cellProps.dataItem?.type)}</td>}
          />
          <GridColumn field="phoneNo" title="Phone" width="110px" />
          <GridColumn field="email" title="Email" />
          <GridColumn field="address" title="Address" />
          <GridColumn field="noOfMatters" title="No of Matters" width="110px" />
          <GridColumn title=" " width="70px" cell={ActionCell} />
        </Grid>
      </div>
    </Tile>
  );
};

const AdditionalClientsTab = ({
  formType,
  leadClient,
  additionalClients = [],
  onChange,
  onSave,
}) => {
  const isView = formType === "VIEW";
  const [viewClient, setViewClient] = useState(null);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [pickerKey, setPickerKey] = useState(0);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const existingIds = [
    leadClient?.clientId,
    ...(additionalClients || []).map((r) => r.clientId),
  ].filter(Boolean);

  const closeAddDialog = () => {
    setShowAddDialog(false);
    setSelectedClient(null);
    setPickerKey((k) => k + 1);
  };

  const handleAddConfirm = async () => {
    if (!selectedClient) return;
    const clientId =
      selectedClient.CustomerID || selectedClient.clientId || selectedClient.clientID;
    if (!clientId) return;

    const newRow = {
      clientId,
      clientRef: selectedClient.clientRef ?? selectedClient.CustomerRef ?? "",
      clientName: selectedClient.clientName ?? selectedClient.ContactName ?? "",
      feeEarnerRef: selectedClient.feeEarnerRef ?? "",
      type: selectedClient.type ?? "",
      phoneNo: selectedClient.phoneNo ?? "",
      email: selectedClient.email ?? "",
      address: selectedClient.address ?? "",
      noOfMatters: selectedClient.noOfMatters,
      order: additionalClients.length,
      active: true,
    };
    const updated = [...(additionalClients || []), newRow];

    setIsAdding(true);
    try {
      await onSave?.(updated);
      onChange?.(updated);
      closeAddDialog();
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveClick = (row) => {
    setClientToDelete(row);
  };

  const confirmRemove = async () => {
    if (!clientToDelete) return;
    const updated = (additionalClients || []).map((r) =>
      r.clientId === clientToDelete.clientId ? { ...r, active: false } : r
    );
    setIsSaving(true);
    try {
      await onSave?.(updated);
      onChange?.(updated);
    } finally {
      setIsSaving(false);
    }
    setClientToDelete(null);
  };

  return (
    <TileWrapper className="h-100 d-flex flex-column">
      <div className="row g-2 flex-grow-1" style={{ minHeight: 0 }}>
        {isView && (
          <div className="col-12 flex-shrink-0">
            <LeadClientInfoTile leadClient={leadClient} />
          </div>
        )}
        <div className="col-12 d-flex" style={{ minHeight: 0, flex: "1 1 auto" }}>
          <div className="w-100 d-flex flex-column">
            <ClientsGridTile
              rows={(additionalClients || []).filter((c) => c.active !== false)}
              isView={isView}
              onRemove={handleRemoveClick}
              onView={(row) => setViewClient(row)}
              onAddClick={() => setShowAddDialog(true)}
            />
          </div>
        </div>
      </div>

      {showAddDialog && (
        <Dialog title="Add Client" onClose={closeAddDialog} width={360}>
          <div className="p-3">
            <Label className="d-block mb-2">Client</Label>
            <ClientComboBox
              key={pickerKey}
              val={selectedClient}
              width="300px"
              height="32px"
              onChangeClient={(c) => setSelectedClient(c)}
              excludeIds={existingIds}
              required={false}
            />
          </div>
          <DialogActionsBar>
            <div className="dialog-footer">
              <Button
                type="button"
                className="common-btn bgBtnSecondary border-0 me-3"
                onClick={closeAddDialog}
              >
                Close
              </Button>
              <SubmitButton
                type="button"
                className="common-btn me-3"
                onClick={handleAddConfirm}
                disabled={!selectedClient}
                isLoading={isAdding}
              >
                Add
              </SubmitButton>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}

      {clientToDelete && (
        <ConfirmationDialog
          content={`Are you sure you want to remove ${clientToDelete.clientName || "this client"} from the matter?`}
          onClickYes={confirmRemove}
          onClickNo={() => setClientToDelete(null)}
          onClose={() => setClientToDelete(null)}
          isLoading={isSaving}
        />
      )}

      {viewClient && (
        <Dialog
          className="dialog-xxl add-dialog"
          title="View Client"
          onClose={() => setViewClient(null)}
        >
          <AddClients
            clientId={viewClient.clientId}
            formId="view"
            title="View Client"
            type="VIEW"
            reference={viewClient.clientRef}
            onClose={() => setViewClient(null)}
          />
          <DialogActionsBar>
            <div className="dialog-footer">
              <Button
                className="common-btn bgBtnSecondary border-0 me-3"
                type="button"
                onClick={() => setViewClient(null)}
              >
                Close
              </Button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </TileWrapper>
  );
};

export default AdditionalClientsTab;
